import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { quotesApi, acordApi } from "@/api/endpoints";
import type { Quote } from "@/api/types";
import { ClientLink } from "@/components/EntityLinks";
import { PageHeader } from "@/components/PageHeader";
import { SimulatedBadge } from "@/components/ProgressiveRaterPanel";
import { formatCurrency, statusClass } from "@/utils/format";

const STATUSES = ["all", "draft", "submitted", "quoted", "proposal_sent", "bound", "declined"];

export function QuotesPage() {
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState<Quote[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function load() {
    quotesApi
      .list(status)
      .then((data) => setItems(data.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load quotes"));
  }

  useEffect(() => {
    load();
  }, [status]);

  async function bindQuote(quoteId: string) {
    setMessage(null);
    try {
      const result = await quotesApi.bind(quoteId);
      setMessage(`Bound to policy ${result.policy_number}`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bind failed");
    }
  }

  return (
    <>
      <PageHeader title="Quotes" subtitle="Active quotes, carrier review, and bind workflow." />
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/rate-shop" className="btn btn-primary">
          Open rate shop
        </Link>
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 220 }}>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s === "all" ? "All statuses" : s.replace(/_/g, " ")}
          </option>
        ))}
      </select>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="card" style={{ padding: "0.75rem", background: "var(--accent-soft)" }}>{message}</div> : null}
      <div className="card">
        {items.length === 0 ? (
          <div className="empty-state">No quotes match this filter.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Product</th>
                <th>Premium</th>
                <th>Status</th>
                <th>Links</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((quote) => (
                <tr key={quote.id}>
                  <td>{quote.quote_number}</td>
                  <td>{quote.product_line}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                      {formatCurrency(quote.premium_cents)}
                      {quote.is_simulated ? <SimulatedBadge /> : null}
                    </div>
                  </td>
                  <td>
                    <span className={statusClass(quote.status)}>{quote.status}</span>
                    {quote.fees_cents || quote.taxes_cents ? (
                      <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginTop: "0.25rem" }}>
                        Premium {formatCurrency(quote.premium_cents)}
                        {quote.fees_cents ? ` + fees ${formatCurrency(quote.fees_cents)}` : ""}
                        {quote.taxes_cents ? ` + taxes ${formatCurrency(quote.taxes_cents)}` : ""}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      {quote.customer_id ? (
                        <ClientLink id={quote.customer_id}>Client 360</ClientLink>
                      ) : null}
                      {quote.lead_id ? (
                        <Link to={`/pipeline?lead=${quote.lead_id}`}>Pipeline</Link>
                      ) : null}
                      {!quote.customer_id && !quote.lead_id ? "—" : null}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                      {["quoted", "proposal_sent", "submitted"].includes(quote.status) ? (
                        <button type="button" className="btn btn-primary" onClick={() => bindQuote(quote.id)}>
                          Bind
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => acordApi.download("quote", quote.id, "125", "pdf")}
                      >
                        ACORD PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
